import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { AuthCard, PrimaryButton } from "@/components/AuthCard";
import { useI18n } from "@/i18n/I18nProvider";
import { api, errorKey } from "@/lib/api";

export default function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [f, setF] = useState({ next: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(token ? "" : "errors.invalid_token");

  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 8) return setError("validation.minPassword");
    if (f.next !== f.confirm) return setError("validation.mismatch");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: f.next });
      setDone(true);
    } catch (err) {
      setError(errorKey(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthCard testId="reset" title={t("auth.resetTitle")} footer={null}>
        <div data-testid="reset-success" role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          <CheckCircle2 className="mb-2 h-6 w-6" /> {t("auth.resetDone")}
        </div>
        <Link to="/login" data-testid="reset-go-login" className="mt-5 flex h-12 items-center justify-center rounded-lg bg-[#000B45] font-bold text-white hover:bg-[#000624]">
          {t("auth.submit")}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard testId="reset" title={t("auth.resetTitle")} subtitle={t("auth.resetSubtitle")}>
      <form onSubmit={submit} className="space-y-5" noValidate>
        {[["next", "settings.newPassword"], ["confirm", "settings.confirmPassword"]].map(([k, label]) => (
          <div key={k} className="space-y-2">
            <Label htmlFor={`reset-${k}`}>{t(label)}</Label>
            <PasswordInput id={`reset-${k}`} testId={`reset-${k}-input`} autoComplete="new-password" value={f[k]}
              onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-12 bg-white text-base" disabled={!token} />
          </div>
        ))}
        {error && <p data-testid="reset-error" role="alert" className="text-sm font-medium text-red-600">{t(error)}</p>}
        <PrimaryButton busy={busy || !token} testId="reset-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.resetSubmit")}
        </PrimaryButton>
        {error === "errors.invalid_token" && (
          <Link to="/forgot-password" data-testid="reset-request-new" className="block text-center text-sm font-semibold text-[#000B45] underline">
            {t("auth.requestNewLink")}
          </Link>
        )}
      </form>
    </AuthCard>
  );
}
